import React, { useEffect, useState } from "react";
import {
    TopbarShell,
    Capsule,
    LeftGroup,
    RightGroup,
    LogoContainer,
    Segment,
    SegBtn,
    NavLinks,
    NavLinkItem,
    SignInButton,
    MobileActions,
} from "./tab-bar.styles";
import Logo from "../../../../assets/images/logo/logo-multicolor.svg";
import useCustomNavigate from "../../../../hooks/useCustomNavigate";
import { colorPalette } from "../../../../utils/theme";
import useIsMobile from "../../../../hooks/useIsMobile";
import MaterialIcon from "../../../../components/material-icon";
import { useLocation } from "react-router-dom";
import useInvestorDetailStore from "../../../../stores/investorDetailStore";
import Sidebar from "../sidebar/sidebar";
import { useContentArea } from "../../../../context/ContentAreaContext";
import { useTranslation } from "react-i18next";
import useTenantDetailStore from "../../../../stores/tenantDetailStore";

interface TabBarProps {
    isDefaultSticky?: boolean;
}

const TabBar: React.FC<TabBarProps> = ({ isDefaultSticky = false }) => {
    const { contentAreaRef } = useContentArea();
    const { t } = useTranslation();
    const navigate = useCustomNavigate();
    const location = useLocation();
    const isMobile = useIsMobile();

    const { investor } = useInvestorDetailStore();
    const { tenant } = useTenantDetailStore();

    const [isScrolled, setIsScrolled] = useState(false);
    const [isSidebarVisible, setIsSidebarVisible] = useState(false);

    useEffect(() => {
        const el = contentAreaRef.current;
        const onScroll = () => setIsScrolled((el?.scrollTop || 0) > 20);
        el?.addEventListener("scroll", onScroll);
        return () => el?.removeEventListener("scroll", onScroll);
    }, [contentAreaRef]);

    const goToDetails = (hash: string) => navigate(`/details#${hash}`);

    const goToTenantHome = (hash: string) => navigate(`/tenant-home#${hash}`);

    const isAuthed = Boolean(investor || tenant);

    return (
        <>
            <TopbarShell className={isDefaultSticky || isScrolled ? "scrolled" : ""}>
                <Capsule>
                    {/* LEFT: logo + segmented control */}
                    <LeftGroup>
                        <LogoContainer onClick={() => navigate("/home")}>
                            <img src={Logo} alt="Managd Logo" draggable={false} />
                        </LogoContainer>

                        {!isMobile && (
                            <Segment role="tablist" aria-label="audiences">
                                <SegBtn
                                    role="tab"
                                    onClick={() => navigate("/home")}
                                    data-active={location.pathname === "/home"}
                                    disabled={location.pathname === "/home"}
                                >
                                    {t("tab_bar.landlords") /* Landlord */}
                                </SegBtn>

                                <SegBtn
                                    role="tab"
                                    onClick={() => navigate("/tenant-home")}
                                    data-active={location.pathname === "/tenant-home"}
                                    disabled={location.pathname === "/tenant-home"}
                                >
                                    {t("tab_bar.tenants") /* Tenants */}
                                </SegBtn>
                            </Segment>
                        )}
                    </LeftGroup>

                    {/* RIGHT: links + sign in (desktop) OR icons (mobile) */}
                    {isMobile ? (
                        <MobileActions>
                            {!location.pathname.includes("/profile") && (
                                <MaterialIcon
                                    icon="account_circle"
                                    color={colorPalette.textMain}
                                    size={24}
                                    onClick={() =>
                                        navigate(isAuthed ? "/profile" : "/login", {
                                            replace: isAuthed,
                                            clearHistory: isAuthed,
                                        })
                                    }
                                />
                            )}
                            <MaterialIcon
                                icon="menu"
                                color={colorPalette.textMain}
                                size={28}
                                onClick={() => setIsSidebarVisible((v) => !v)}
                            />
                        </MobileActions>
                    ) : (
                        <RightGroup>
                            {(location.pathname === "/home" || location.pathname === "/details") && (
                                <NavLinks>
                                    <NavLinkItem onClick={() => goToDetails("services")}>
                                        {t("tab_bar.services")}
                                    </NavLinkItem>
                                    <NavLinkItem onClick={() => goToDetails("packages")}>
                                        {t("tab_bar.packages")}
                                    </NavLinkItem>
                                    <NavLinkItem onClick={() => goToDetails("faq")}>
                                        {t("tab_bar.faq")}
                                    </NavLinkItem>
                                </NavLinks>
                            )}
                            {location.pathname === "/tenant-home" && (
                                <NavLinks>
                                    <NavLinkItem onClick={() => goToTenantHome("available_units")}>
                                        {t("tab_bar.available_units")}
                                    </NavLinkItem>
                                    <NavLinkItem onClick={() => goToTenantHome("how_it_works")}>
                                        {t("tab_bar.how_it_works")}
                                    </NavLinkItem>
                                    {/*<NavLinkItem onClick={() => goToTenantHome("faq")}>
                                        {t("tab_bar.faq")}
                                    </NavLinkItem>*/}
                                </NavLinks>
                            )}

                            <SignInButton
                                onClick={() =>
                                    navigate(isAuthed ? "/dashboard" : "/login", {
                                        replace: isAuthed,
                                        clearHistory: isAuthed,
                                    })
                                }
                            >
                                {isAuthed ? t("tab_bar.to_dashboard") : t("tab_bar.login")}
                            </SignInButton>
                        </RightGroup>
                    )}
                </Capsule>
            </TopbarShell>

            {isMobile && (
                <Sidebar
                    isSidebarVisible={isSidebarVisible}
                    toggleSidebar={() => setIsSidebarVisible((v) => !v)}
                />
            )}
        </>
    );
};

export default TabBar;
